import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiX } from "react-icons/hi";
import moment from "moment";
import { isActionReasonValid } from "./tenantDetail.utils";

const TenantExtendTrialModal = ({
  open,
  onClose,
  currentTrialEndDate = null,
  submitting = false,
  onSubmit,
}) => {
  const [reason, setReason] = useState("");
  const [trialEndDate, setTrialEndDate] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    const suggested = currentTrialEndDate
      ? moment(currentTrialEndDate).add(14, "days")
      : moment().add(14, "days");

    setTrialEndDate(suggested.format("YYYY-MM-DD"));
    setReason("");
    setError("");
  }, [currentTrialEndDate, open]);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!trialEndDate || !moment(trialEndDate, "YYYY-MM-DD", true).isValid()) {
      setError("Choose a valid trial end date.");
      return;
    }

    if (!moment(trialEndDate).isAfter(moment(), "day")) {
      setError("Trial end date must be in the future.");
      return;
    }

    if (!isActionReasonValid(reason)) {
      setError("Reason must be at least 10 characters.");
      return;
    }

    setError("");
    await onSubmit({ reason: reason.trim(), trialEndDate });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/40" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all">
                <form onSubmit={handleSubmit}>
                  <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
                    <div>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Extend trial
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        Updates Vertex360 and Stripe subscription trial end when configured.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                      <HiX className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4 px-5 py-4">
                    <div>
                      <label htmlFor="trial-end-date" className="block text-sm font-medium text-gray-700">
                        New trial end date
                      </label>
                      <input
                        id="trial-end-date"
                        type="date"
                        value={trialEndDate}
                        onChange={(event) => setTrialEndDate(event.target.value)}
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="extend-trial-reason" className="block text-sm font-medium text-gray-700">
                        Reason
                      </label>
                      <textarea
                        id="extend-trial-reason"
                        rows={4}
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="Minimum 10 characters for audit log"
                      />
                    </div>

                    {error ? <p className="text-sm text-red-600">{error}</p> : null}
                  </div>

                  <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={submitting}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {submitting ? "Saving…" : "Extend trial"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default TenantExtendTrialModal;
